import { AfterViewInit, Component, ElementRef, HostListener, Input, OnChanges, OnDestroy, QueryList, SimpleChanges, ViewChild, ViewChildren } from '@angular/core';
import { ItemSimplesView } from 'src/app/persistences/item-simples-view';
import { PesquisaService } from 'src/app/services/pesquisa.service';
import { SearchbarService } from 'src/app/services/searchbar.service';
import { CartSingletonService } from 'src/app/singletons/cart-singleton.service';
import { LocalStorageSingletonService } from 'src/app/singletons/local-storage-singleton.service';
import { LinhaTabelaItemComponent } from '../linha-tabela-item/linha-tabela-item.component';

@Component({
  standalone: false,
  selector: 'app-tabela-lista-itens',
  templateUrl: './tabela-lista-itens.component.html',
  styleUrls: ['./tabela-lista-itens.component.css'],
})
export class TabelaListaItensComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input()
  public listaItens: ItemSimplesView[] = [];
  @Input()
  public totalItens = 0;
  @Input()
  public page = 1;
  @Input()
  public pagesize = 20;

  @ViewChildren(LinhaTabelaItemComponent)
  private linhas: QueryList<LinhaTabelaItemComponent>;

  @ViewChild('scroll')
  private scroll: ElementRef<HTMLElement>;

  /** Altura máxima da área com scroll: do topo da tabela até o fim da janela, reservando espaço para a paginação. */
  public alturaMaxima = 600;

  private timerAltura: ReturnType<typeof setTimeout>;
  private timerRolagem: ReturnType<typeof setTimeout>;

  public selecionadoId: number | null = null;
  public expandidoId: number | null = null;
  public expandirAoNavegar: boolean;

  constructor(
    private cartSingletonService: CartSingletonService,
    private pesquisaService: PesquisaService,
    private searchbarService: SearchbarService,
    private localStorageSingletonService: LocalStorageSingletonService
  ) {
    this.expandirAoNavegar = this.localStorageSingletonService.buscarTabelaExpandirAoNavegar();
  }

  ngOnDestroy(): void {
    clearTimeout(this.timerAltura);
    clearTimeout(this.timerRolagem);
  }

  ngAfterViewInit(): void {
    // Aguarda o layout da página assentar (banners, barras) antes de medir a posição da tabela.
    this.timerAltura = setTimeout(() => this.calcularAlturaMaxima());
  }

  @HostListener('window:resize')
  calcularAlturaMaxima(): void {
    if (!this.scroll) {
      return;
    }
    const topo = this.scroll.nativeElement.getBoundingClientRect().top;
    const reservaPaginacao = 70;
    this.alturaMaxima = Math.max(300, Math.floor(window.innerHeight - topo - reservaPaginacao));
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Nova página/pesquisa: descarta seleção e expansão de itens que não estão mais na lista.
    if (changes['listaItens']) {
      if (!this.listaItens?.some((i) => i.idItem === this.selecionadoId)) {
        this.selecionadoId = null;
      }
      if (!this.listaItens?.some((i) => i.idItem === this.expandidoId)) {
        this.expandidoId = null;
      }
    }
  }

  // ---- Cabeçalho informativo ----

  get termoPesquisado(): string {
    return this.searchbarService.getIsPesquisaSearchbar() ? this.pesquisaService.getTermoPesquisado() : '';
  }

  get qtdItensNoPedido(): number {
    return this.cartSingletonService.getQtdItensNoCarrinho();
  }

  get totalDoPedido(): number {
    return this.cartSingletonService.getCarrinhoEstatico()?.getTotal ?? 0;
  }

  alterarExpandirAoNavegar(valor: boolean): void {
    this.expandirAoNavegar = valor;
    this.localStorageSingletonService.atualizarTabelaExpandirAoNavegar(valor);
  }

  // ---- Interação com o mouse ----

  selecionar(item: ItemSimplesView): void {
    this.selecionadoId = item.idItem;
  }

  alternarExpansao(item: ItemSimplesView): void {
    this.selecionadoId = item.idItem;
    this.expandidoId = this.expandidoId === item.idItem ? null : item.idItem;
    this.rolarParaSelecionado();
  }

  // ---- Navegação por teclado ----

  @HostListener('document:keydown', ['$event'])
  onKeydown(evento: KeyboardEvent): void {
    if (evento.defaultPrevented || evento.ctrlKey || evento.altKey || evento.metaKey || !this.listaItens?.length) {
      return;
    }
    if (this.digitandoOuComOverlayAberto(evento)) {
      return;
    }
    // Chamado #73959: tecla segurada (auto-repeat) dispararia várias alterações de quantidade sobrepostas
    // (cada uma recalcula o item e grava o pedido); cada toque em "+"/"-" vale um passo.
    if (evento.repeat && (evento.key === '+' || evento.key === '-')) {
      return;
    }

    const indiceAtual = this.listaItens.findIndex((i) => i.idItem === this.selecionadoId);
    switch (evento.key) {
      case 'ArrowDown':
        this.moverPara(indiceAtual < 0 ? 0 : Math.min(indiceAtual + 1, this.listaItens.length - 1), true);
        break;
      case 'ArrowUp':
        this.moverPara(indiceAtual < 0 ? 0 : Math.max(indiceAtual - 1, 0), true);
        break;
      case 'Home':
        this.moverPara(0, true);
        break;
      case 'End':
        this.moverPara(this.listaItens.length - 1, true);
        break;
      case 'ArrowRight':
        this.moverPara(Math.max(indiceAtual, 0), false);
        this.expandidoId = this.selecionadoId;
        break;
      case 'ArrowLeft':
      case 'Escape':
        if (this.expandidoId === null) {
          return;
        }
        this.expandidoId = null;
        break;
      case '+':
        if (!this.linhaSelecionada()) {
          return;
        }
        this.linhaSelecionada().incrementar();
        break;
      case '-':
        if (!this.linhaSelecionada()) {
          return;
        }
        this.linhaSelecionada().decrementar();
        break;
      case 'Enter':
        // Em botões o Enter já ativa o próprio botão.
        if (!this.linhaSelecionada() || this.alvoEhBotaoOuLink(evento)) {
          return;
        }
        this.linhaSelecionada().adicionar();
        break;
      default:
        return;
    }
    evento.preventDefault();
  }

  private moverPara(indice: number, respeitarExpansaoAutomatica: boolean): void {
    const item = this.listaItens[indice];
    if (!item) {
      return;
    }
    this.selecionadoId = item.idItem;
    if (respeitarExpansaoAutomatica && this.expandirAoNavegar) {
      this.expandidoId = item.idItem;
    }
    this.rolarParaSelecionado();
  }

  private linhaSelecionada(): LinhaTabelaItemComponent | undefined {
    return this.linhas?.find((linha) => linha.itemPesquisa.idItem === this.selecionadoId);
  }

  /** Mantém a linha selecionada visível dentro da área com scroll da tabela. */
  private rolarParaSelecionado(): void {
    clearTimeout(this.timerRolagem);
    this.timerRolagem = setTimeout(() => {
      this.linhaSelecionada()?.elemento.nativeElement.scrollIntoView({ block: 'nearest' });
    });
  }

  /** Os atalhos não podem interferir na digitação (ex.: campo de quantidade) nem em diálogos/menus abertos. */
  private digitandoOuComOverlayAberto(evento: KeyboardEvent): boolean {
    const alvo = evento.target as HTMLElement | null;
    const tag = alvo?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || alvo?.isContentEditable) {
      return true;
    }
    return !!document.querySelector('.cdk-overlay-container .mat-mdc-dialog-container, .cdk-overlay-container .mat-mdc-menu-panel');
  }

  private alvoEhBotaoOuLink(evento: KeyboardEvent): boolean {
    const tag = (evento.target as HTMLElement | null)?.tagName;
    return tag === 'BUTTON' || tag === 'A';
  }
}
